import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Paper, Typography, Grid, TextField, Button, Stack, List, ListItem, ListItemText, Divider, Alert, CircularProgress, Snackbar, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PageNavBar from "../components/PageNavBar";
import { useUserContext } from "../context/UserContext";
import { createClassroom, listClassrooms, updateClassroom, deleteClassroom } from "../context/Api";
import QRCodeGenerator from "../components/QRCodeGenerator";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { firestore } from "../firebase.config";

const ClassroomManagement = () => {
  const { userRole, tenantId, selectedTenantId, setSelectedTenantId, setSelectedTenantName, refreshContext } = useUserContext();
  const isAdmin = userRole === "admin" || userRole === "SuperAdmin";
  const navigate = useNavigate();
  
  // For SuperAdmin, use selectedTenantId; for regular users, use tenantId
  const effectiveTenantId = userRole === "SuperAdmin" ? selectedTenantId : tenantId;
  const [name, setName] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState("100");
  const [classrooms, setClassrooms] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", name: "", latitude: "", longitude: "", radius: "" });

  const fetchClassrooms = async () => {
    if (!effectiveTenantId) {
      console.log("No effectiveTenantId available for fetching classrooms");
      setError("No tenant ID available. Please select a tenant or refresh the page.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      console.log("Fetching classrooms for effectiveTenantId:", effectiveTenantId);
      const list = await listClassrooms(effectiveTenantId);
      console.log("Fetched classrooms:", list);
      setClassrooms(list || []);
    } catch (err) {
      console.error("Error fetching classrooms:", err);
      setError("Failed to fetch classrooms: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("ClassroomManagement: effectiveTenantId changed to:", effectiveTenantId);
    console.log("ClassroomManagement: userRole:", userRole);
    console.log("ClassroomManagement: isAdmin:", isAdmin);
    console.log("ClassroomManagement: tenantId:", tenantId);
    console.log("ClassroomManagement: selectedTenantId:", selectedTenantId);
    fetchClassrooms();
  }, [effectiveTenantId]);

  const handleRefresh = () => {
    refreshContext();
    fetchClassrooms();
  };

  const handleClearForm = () => {
    setName("");
    setLatitude("");
    setLongitude("");
    setRadius("100");
    setSelectedId("");
    setError("");
    setSuccess("");
  };

  const openEditClassroom = (c) => {
    setEditForm({
      id: c.id,
      name: c.name || "",
      latitude: String(c.latitude ?? ""),
      longitude: String(c.longitude ?? ""),
      radius: String(c.radius ?? "")
    });
    setEditOpen(true);
  };

  const saveEditClassroom = async () => {
    try {
      if (!editForm.id) return;
      await updateClassroom(editForm.id, {
        name: editForm.name.trim(),
        latitude: Number(editForm.latitude),
        longitude: Number(editForm.longitude),
        radius: Number(editForm.radius) || 100,
      });
      setSuccess("Classroom updated");
      setEditOpen(false);
      const list = await listClassrooms(effectiveTenantId);
      setClassrooms(list || []);
    } catch (err) {
      setError(err.message || "Failed to update classroom");
    }
  };

  const confirmDeleteClassroom = async (c) => {
    try {
      if (!effectiveTenantId) throw new Error("No tenant ID");
      await deleteClassroom(effectiveTenantId, c.id);
      setSuccess("Classroom deleted");
      const list = await listClassrooms(effectiveTenantId);
      setClassrooms(list || []);
      if (selectedId === c.id) setSelectedId("");
    } catch (err) {
      setError(err.message || "Failed to delete classroom");
    }
  };

  // Auto-select tenant for admin users
  useEffect(() => {
    if (userRole === "admin" && tenantId && !selectedTenantId) {
      // Automatically set tenant for admin users
      setSelectedTenantId(tenantId);
      sessionStorage.setItem("selectedTenantId", tenantId);
    }
  }, [userRole, tenantId, selectedTenantId, setSelectedTenantId]);

  // Fetch tenants for SuperAdmin
  useEffect(() => {
    const fetchTenants = async () => {
      if (userRole === "SuperAdmin") {
        setTenantsLoading(true);
        try {
          const snapshot = await getDocs(collection(firestore, "TenantMaster"));
          const tenants = snapshot.docs.map((doc) => doc.data());
          setTenantList(tenants);
        } catch (err) {
          console.error("Error fetching tenants:", err);
          setError("Failed to fetch tenants: " + (err.message || "Unknown error"));
        } finally {
          setTenantsLoading(false);
        }
      }
    };
    fetchTenants();
  }, [userRole]);

  // Handle tenant selection for SuperAdmin
  const handleTenantChange = (e) => {
    const selectedId = e.target.value;
    const selected = tenantList.find((t) => t.tenantId === selectedId);
    
    setSelectedTenantId(selectedId);
    setSelectedTenantName(selected?.tenantName || "");
    
    // Save to sessionStorage so it persists
    sessionStorage.setItem("selectedTenantId", selectedId);
    sessionStorage.setItem("selectedTenantName", selected?.tenantName || "");
    
    // Clear any existing error
    setError("");
  };

  const handleCreate = async () => {
    if (!isAdmin) {
      setError("Only administrators can create classrooms");
      return;
    }
    
    if (!name || !latitude || !longitude) {
      setError("Please fill in all required fields");
      return;
    }
    
    if (!effectiveTenantId) {
      setError("No tenant ID available. Please select a tenant or refresh and try again.");
      return;
    }
    
    setCreating(true);
    setError("");
    
    try {
      const payload = {
        name: name.trim(),
        latitude: Number(latitude),
        longitude: Number(longitude),
        radius: Number(radius) || 100,
        tenantId: effectiveTenantId,
        createdBy: sessionStorage.getItem("userEmail") || "",
      };
      
      console.log("Creating classroom with payload:", payload);
      const { id } = await createClassroom(payload);
      console.log("Classroom created with ID:", id);
      
      // Reset form
      setName("");
      setLatitude("");
      setLongitude("");
      setRadius("100");
      setSelectedId(id);
      setSuccess("Classroom created successfully!");
      
      // Refresh the list
      const list = await listClassrooms(effectiveTenantId);
      setClassrooms(list || []);
      
    } catch (err) {
      console.error("Error creating classroom:", err);
      setError("Failed to create classroom: " + (err.message || "Unknown error"));
    } finally {
      setCreating(false);
    }
  };

  // Generate QR payload with time encoding to prevent proxy
  const qrPayload = useMemo(() => {
    if (!selectedId) return "";
    const timestamp = Date.now();
    return JSON.stringify({ t: "classroom", id: selectedId, ts: timestamp });
  }, [selectedId]);

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", py: 4, position: "relative" }}>
      <PageNavBar title="Classroom Management" backTo="/dashboard" />
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "rgba(255, 255, 255, 0.98)" }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Classroom Management</Typography>
          
          
          
          {/* Error and Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          
          {userRole === "SuperAdmin" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>SuperAdmin:</strong> Select a tenant to manage classrooms for that organization.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Select Tenant</InputLabel>
                <Select
                  value={selectedTenantId || ""}
                  onChange={handleTenantChange}
                  label="Select Tenant"
                  disabled={tenantsLoading}
                >
                  {tenantsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading tenants...
                    </MenuItem>
                  ) : (
                    tenantList.map((tenant) => (
                      <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.tenantName || tenant.tenantId}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <TextField 
                  label="Classroom Name" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  fullWidth 
                  disabled={creating}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      label="Latitude" 
                      value={latitude} 
                      onChange={(e) => setLatitude(e.target.value)} 
                      fullWidth 
                      disabled={creating}
                      type="number"
                      inputProps={{ step: "any" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      label="Longitude" 
                      value={longitude} 
                      onChange={(e) => setLongitude(e.target.value)} 
                      fullWidth 
                      disabled={creating}
                      type="number"
                      inputProps={{ step: "any" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      label="Radius (meters)" 
                      value={radius} 
                      onChange={(e) => setRadius(e.target.value)} 
                      fullWidth 
                      disabled={creating}
                      type="number"
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={2}>
                  <Button 
                    variant="contained" 
                    disabled={!isAdmin || !name || !latitude || !longitude || creating} 
                    onClick={handleCreate}
                    startIcon={creating ? <CircularProgress size={20} /> : null}
                    sx={{ flex: 1 }}
                  >
                    {creating ? "Creating..." : "Create Classroom"}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleClearForm}
                    disabled={creating}
                  >
                    Clear
                  </Button>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Existing Classrooms</Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleRefresh}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                  >
                    Refresh
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : classrooms.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">No classrooms found</Typography>
                    </Box>
                  ) : (
                    <List dense>
                      {classrooms.map(c => (
                        <ListItem 
                          key={c.id} 
                          button 
                          onClick={() => {
                            setSelectedId(c.id);
                            navigate(`/timetable/${c.id}`);
                          }} 
                          selected={selectedId === c.id}
                          sx={{ 
                            '&:hover': { backgroundColor: 'action.hover' },
                            '&.Mui-selected': { backgroundColor: 'primary.light' }
                          }}
                        >
                          <ListItemText 
                            primary={c.name || `Classroom ${c.id}`} 
                            secondary={`Lat: ${c.latitude}, Lng: ${c.longitude}, Radius: ${c.radius}m`} 
                          />
                          {isAdmin && (
                            <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditClassroom(c); }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); confirmDeleteClassroom(c); }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              {qrPayload ? (
                <QRCodeGenerator 
                  value={qrPayload} 
                  size={280} 
                  eventName={classrooms.find(x => x.id === selectedId)?.name || "Classroom QR"} 
                />
              ) : (
                <Box sx={{ 
                  height: 400, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 2, 
                  border: '2px dashed #ccc' 
                }}>
                  <Typography color="text.secondary">
                    Select a classroom to view its QR code
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Container>
      
      {/* Snackbar for additional feedback */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
        message={success}
      />
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Classroom</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Classroom Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} fullWidth />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="Latitude" type="number" value={editForm.latitude} onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })} fullWidth inputProps={{ step: "any" }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Longitude" type="number" value={editForm.longitude} onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })} fullWidth inputProps={{ step: "any" }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Radius (meters)" type="number" value={editForm.radius} onChange={(e) => setEditForm({ ...editForm, radius: e.target.value })} fullWidth />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditClassroom}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassroomManagement;



